import type { UIMessage } from 'ai';
import type { Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import { getFunctionsUrl } from './get-functions-url';
import { getLastToolPart } from './llm-message-utils';

// `headers` is async so it becomes the single choke point every agent request (including
// mid-run continuations and stream reconnects) resolves a token through: HttpChatTransport awaits
// a Resolvable `headers` on every `sendMessages`/`reconnectToStream` call, not just once at
// construction. A missing token throws here rather than being gated elsewhere, so the failure
// surfaces through the transport/chat's normal error path.
export const getAgentTransportConfig = (getAccessToken: () => Promise<string | null>, environment: Record<string, unknown>) => ({
  api: getFunctionsUrl('tiptapAgent', environment),
  headers: async () => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  },
});

type ExecuteTiptapToolParams = {
  editor: Editor;
  toolCallId: string;
  toolName: string;
  input: unknown;
};

type ExecuteTiptapToolResult = {
  output: unknown;
};

export const executeTiptapTool = ({ editor, toolCallId, toolName, input }: ExecuteTiptapToolParams): ExecuteTiptapToolResult => {
  try {
    const toolkit = getAiToolkit(editor);
    return {
      output: toolkit.streamTool({
        toolCallId,
        toolName,
        input,
        hasFinished: true,
      }).output,
    };
  } catch (err) {
    return { output: { error: err instanceof Error ? err.message : 'Failed to execute tool' } };
  }
};

type PendingStreamTool = {
  toolCallId: string;
  toolName: string;
  input: unknown;
};

export const getPendingStreamTool = (messages: UIMessage[]): PendingStreamTool | undefined => {
  const lastMessage = messages.at(-1);
  const lastTool = lastMessage && lastMessage.role === 'assistant' ? getLastToolPart(lastMessage) : null;

  if (lastTool && lastTool.state === 'input-streaming') {
    return {
      toolCallId: lastTool.toolCallId,
      toolName: lastTool.type.replace('tool-', ''),
      input: lastTool.input,
    };
  }
};
